import { getPreferredClient } from '@/lib/auth';
import { OnboardingState } from './types';

const ONBOARDING_STATE_KEY = 'onboarding_state';
const ONBOARDING_STATE_STORAGE_PREFIX = 'watchful-onboarding-state';

function getStorageKey(userId: string): string {
  return `${ONBOARDING_STATE_STORAGE_PREFIX}-${userId}`;
}

function getDefaultOnboardingState(): OnboardingState {
  return {
    welcomeDismissed: false,
    completed: {},
  };
}

/**
 * Get onboarding state from localStorage cache
 */
function getOnboardingStateFromCache(userId: string): OnboardingState {
  const key = getStorageKey(userId);
  const cached = localStorage.getItem(key);
  if (cached) {
    try {
      return JSON.parse(cached);
    } catch (error) {
      console.warn('[Onboarding] Failed to parse cached state:', error);
    }
  }
  return getDefaultOnboardingState();
}

/**
 * Save onboarding state to localStorage cache
 */
function saveOnboardingStateToCache(userId: string, state: OnboardingState): void {
  const key = getStorageKey(userId);
  localStorage.setItem(key, JSON.stringify(state));
}

/**
 * Check if onboarding is completed (synchronous check from localStorage cache)
 * For cross-device sync, use hasCompletedOnboardingAsync instead
 */
export function hasCompletedOnboarding(processName: string, userId: string): boolean {
  const state = getOnboardingStateFromCache(userId);
  return state.completed[processName] === true;
}

/**
 * Get onboarding state from backend (async)
 * This ensures cross-device synchronization
 */
export async function getOnboardingStateFromBackend(userId: string): Promise<OnboardingState> {
  try {
    const response = await getPreferredClient().get(`/api/UserPreferences/${ONBOARDING_STATE_KEY}`);

    if (response.status === 200 && response.data.preference?.value) {
      const state = typeof response.data.preference.value === 'string'
        ? JSON.parse(response.data.preference.value)
        : response.data.preference.value;
      
      // Update localStorage cache
      saveOnboardingStateToCache(userId, state);
      return state;
    }

    // If not found in backend, return cached or default state
    return getOnboardingStateFromCache(userId);
  } catch (error) {
    // If API call fails, fall back to localStorage
    // Don't log warnings for aborted requests (expected when component unmounts)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isAborted = (error as any)?.code === 'ECONNABORTED' || (error as any)?.message === 'Request aborted';
    if (!isAborted) {
      console.warn('[Onboarding] Failed to get state from backend, using localStorage:', error);
    }
    return getOnboardingStateFromCache(userId);
  }
}

/**
 * Check if onboarding is completed from backend (async)
 * This ensures cross-device synchronization
 */
export async function hasCompletedOnboardingAsync(
  processName: string,
  userId: string
): Promise<boolean> {
  const state = await getOnboardingStateFromBackend(userId);
  return state.completed[processName] === true;
}

/**
 * Save onboarding state to backend
 */
export async function saveOnboardingStateToBackend(
  _userId: string,
  state: OnboardingState
): Promise<void> {
  try {
    await getPreferredClient().post('/api/UserPreferences', {
      key: ONBOARDING_STATE_KEY,
      value: JSON.stringify(state),
    });
  } catch (error) {
    console.error('[Onboarding] Failed to save state to backend:', error);
    throw error;
  }
}

/**
 * Mark onboarding as complete
 * Saves to both localStorage (for immediate access) and backend (for cross-device sync)
 */
export async function markOnboardingComplete(
  processName: string,
  userId: string
): Promise<void> {
  // Get current state
  const state = getOnboardingStateFromCache(userId);
  
  // Update completion flag
  state.completed[processName] = true;

  // Update localStorage immediately for synchronous checks
  saveOnboardingStateToCache(userId, state);

  // Sync with backend for cross-device persistence
  try {
    await saveOnboardingStateToBackend(userId, state);
  } catch (error) {
    console.error('[Onboarding] Failed to save completion to backend:', error);
    // Continue even if backend save fails - localStorage is still updated
  }
}

/**
 * Reset onboarding completion
 * Removes from both localStorage and backend
 */
export async function resetOnboarding(processName: string, userId: string): Promise<void> {
  // Get current state
  const state = getOnboardingStateFromCache(userId);
  
  // Reset completion flag
  state.completed[processName] = false;

  // Update localStorage
  saveOnboardingStateToCache(userId, state);

  // Update backend
  try {
    await saveOnboardingStateToBackend(userId, state);
  } catch (error) {
    console.error('[Onboarding] Failed to reset completion in backend:', error);
    // Continue even if backend reset fails
  }
}

/**
 * Sync onboarding state from backend to localStorage
 * Call this on app initialization to ensure localStorage is up to date
 */
export async function syncOnboardingFromBackend(userId: string): Promise<void> {
  try {
    // getOnboardingStateFromBackend already updates localStorage
    await getOnboardingStateFromBackend(userId);
  } catch (error) {
    // Don't log errors for aborted requests (expected when component unmounts)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isAborted = (error as any)?.code === 'ECONNABORTED' || (error as any)?.message === 'Request aborted';
    if (!isAborted) {
      console.error('[Onboarding] Failed to sync from backend:', error);
    }
  }
}

/**
 * Mark welcome modal as dismissed
 */
export async function markWelcomeDismissed(userId: string): Promise<void> {
  const state = getOnboardingStateFromCache(userId);
  state.welcomeDismissed = true;
  state.welcomeDismissedAt = new Date().toISOString();
  saveOnboardingStateToCache(userId, state);

  try {
    await saveOnboardingStateToBackend(userId, state);
  } catch (error) {
    console.error('[Onboarding] Failed to save welcome dismissed to backend:', error);
  }
}

/**
 * Check if welcome modal has been dismissed
 */
export function hasWelcomeDismissed(userId: string): boolean {
  const state = getOnboardingStateFromCache(userId);
  return state.welcomeDismissed === true;
}

/**
 * Get the timestamp when welcome was dismissed
 * Returns null if not set (for backwards compatibility with existing users)
 */
export function getWelcomeDismissedAt(userId: string): string | null {
  const state = getOnboardingStateFromCache(userId);
  return state.welcomeDismissedAt ?? null;
}

/**
 * Mark all onboarding tours as complete
 * Used when user clicks close (X) on any tour to prevent all tours from auto-appearing
 */
export async function markAllOnboardingComplete(userId: string, allProcessNames: string[]): Promise<void> {
  const state = getOnboardingStateFromCache(userId);
  
  // Mark all tours as completed
  allProcessNames.forEach(processName => {
    state.completed[processName] = true;
  });

  // Update localStorage immediately
  saveOnboardingStateToCache(userId, state);

  // Sync with backend
  try {
    await saveOnboardingStateToBackend(userId, state);
  } catch (error) {
    console.error('[Onboarding] Failed to save all completions to backend:', error);
  }
}

/**
 * Reset all onboarding state for a user
 * Clears both localStorage and backend
 * Used when onboarding_required is reset to 'true'
 */
export async function resetAllOnboardingCompletion(userId: string): Promise<void> {
  const defaultState = getDefaultOnboardingState();
  
  // Clear localStorage
  saveOnboardingStateToCache(userId, defaultState);

  // Reset backend
  try {
    await saveOnboardingStateToBackend(userId, defaultState);
  } catch (error) {
    console.error('[Onboarding] Failed to reset all onboarding in backend:', error);
  }
}

